export interface CoursesUnitsList {
  items: CourseUnit[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface CourseUnit {
  id: string;
  courseUnitNovadisId: string;
  name: string;
  description?: string;
  durationInMinutes?: number;
  numberOfPhases?: number;
  internalComments?: string;
  novadisCode?: string;
  createAt: string;
  updateAt: string;
  chapters: Chapter[];
  nbchapters: number;
  lastRefreshedAt?: any;
}

export interface Chapter {
  id: string;
  chapterNovadisId: string;
  name: string;
  description?: string;
  entTooltip?: string;
  displayed: boolean;
  createdAt: string;
  updatedAt: string;
  subchapters: Chapter[];
  courseContents: CourseContent[];
  quizzes: any[];
}

export interface CourseContentList {
  items: CourseContent[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface CourseContent {
  id: string;
  contentNovadisId: string;
  name: string;
  elearningProvider: string;
  description?: string;
  releaseDate?: string;
  learningSubjectId?: string;
  printDate?: string;
  stopDate?: string;
  durationInMinutes?: number;
  createdAt: string;
  updatedAt: string;
  assessments: [];
  medias: [];
  externalCourseContents: ExternalCourseContents[];
  phase: number;
  contentTypes: string[];
  order: number;
  hasAssimilationQuiz: boolean;
  displayableInDemo: boolean;
  isAdditionalContent: boolean;
  provenanceId: string;
  provenanceExportId: string;
}

export interface ExternalCourseContents {
  id: string;
  name: string;
  url: string;
  format: string;
  provider: string;
  externalContentId: string;
  novadisExternalId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  creator: string;
  courseContent: any;
  order: number;
}

export interface Media {
  id: string;
  novadisId: string;
  name: string;
  type: string;
  url: string;
  createdAt: string;
  updatedAt: string;
  lastRefreshedAt: string;
  hasAssimilationQuiz: boolean;
  courseIds: string[];
  contentType: string;
  assimilationQuizIds: any[];
  hasEmbeddings: boolean;
  subtitlesUrl: string;
}

export interface ClassroomsList {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  items: Classrooms[];
}

export interface Classrooms {
  id: number;
  name: string;
  description: string;
  url: string;
}

export enum ExternalContentProvider {
  Tous = 0,
  Apolearn = 1,
  Novadis = 2,
  Novadream = 3,
  Rise = 4,
}

export enum ContentProvider {
  Tous = 0,
  Apolearn = 1,
  Rise = 2,
  Novadis = 3,
}

export enum CoursesUnitsScreen {
  coursesUnits = 0,
  coursesUnitsChapter = 1,
  coursesUnitsSubChapter = 2,
  coursesUnitsContent = 4,
  coursesContent = 5,
  externalCourseContent = 6,
}

export enum keyDefs {
  String = 0,
  Number = 1,
  Select = 2,
  Boolean = 3,
  Date = 4,
  DatePicker = 5,
}

export interface Property {
  visible: boolean;
  keyName: string;
  keyValue: any;
  keyLabel: string;
  keyDef: keyDefs;
  keyUnit: string;
  keyTable?: any[];
  alone: boolean;
  readOnly?: boolean;
  multiline?: boolean;
  maxLenght?: number;
  copiable?: boolean;
  tooltip?: string;
  placeholder?: string;
}

export enum ElearningProvider {
  AlterCampus = 1,
  IcdlEval = 2,
  PhotothequeAnimaux = 3,
  PhotothequeNature = 4,
  PixPlay = 5,
  ModuleSCORM = 6,
  SeriousFactory = 7,
  SeriousPlay = 8,
  SeriousSkills = 9,
}

export enum ImportProvider {
  Apolearn = 1,
  Novadis = 2,
}

export const CoursesUnitsMetaData = (listOfSkills: TableKeyValue[]): Property[] => {
  return [
    {
      visible: false,
      keyName: 'id',
      keyLabel: 'Id',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: false,
      keyName: 'courseUnitNovadisId',
      keyLabel: 'Code Novadis',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'novadisCode',
      keyLabel: 'Code Novadis',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
      copiable: true,
    },
    {
      visible: true,
      keyName: 'name',
      keyLabel: 'Nom',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: false,
    },
    {
      visible: true,
      keyName: 'description',
      keyLabel: 'Description',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: false,
      multiline: true,
    },
    {
      visible: true,
      keyName: 'internalComments',
      keyLabel: 'Observation (Novadis)',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: false,
      multiline: true,
    },
    {
      visible: true,
      keyName: 'durationInMinutes',
      keyLabel: 'Durée',
      keyDef: keyDefs.Number,
      keyUnit: 'min',
      keyValue: null,
      alone: true,
      readOnly: false,
    },
    {
      visible: true,
      keyName: 'numberOfPhases',
      keyLabel: 'Nombre de phases',
      keyDef: keyDefs.Number,
      keyUnit: '',
      keyValue: null,
      alone: true,
      readOnly: false,
    },
    {
      visible: false,
      keyName: 'skills',
      keyLabel: 'Compétences',
      keyDef: keyDefs.Select,
      keyTable: listOfSkills,
      keyUnit: '',
      keyValue: 0,
      alone: true,
      readOnly: false,
    },
    {
      visible: true,
      keyName: 'createdAt',
      keyLabel: 'Créé le',
      keyDef: keyDefs.Date,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'updatedAt',
      keyLabel: 'Modifié le',
      keyDef: keyDefs.Date,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
  ];
};

export interface TableKeyValue {
  id: string;
  name: string;
  novadisCode?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const CoursesUnitsChapterMetaDatas: Property[] = [
  {
    visible: false,
    keyName: 'id',
    keyLabel: 'Id',
    keyDef: keyDefs.String,
    keyUnit: '',
    keyValue: '',
    alone: true,
    readOnly: true,
  },
  {
    visible: true,
    keyName: 'chapterNovadisId',
    keyLabel: 'Code Novadis',
    keyDef: keyDefs.String,
    keyUnit: '',
    keyValue: '',
    alone: true,
    readOnly: true,
  },
  {
    visible: true,
    keyName: 'name',
    keyLabel: 'Nom',
    keyDef: keyDefs.String,
    keyUnit: '',
    keyValue: 0,
    alone: true,
  },
  {
    visible: false,
    keyName: 'description',
    keyLabel: 'Description',
    keyDef: keyDefs.String,
    keyUnit: '',
    keyValue: '',
    alone: false,
    readOnly: false,
    multiline: true,
  },
  {
    visible: true,
    keyName: 'displayed',
    keyLabel: 'Afficher',
    keyDef: keyDefs.Boolean,
    keyUnit: '',
    keyValue: true,
    alone: false,
    readOnly: false,
  },
  {
    visible: true,
    keyName: 'entTooltip',
    keyLabel: 'Tooltip Ent',
    placeholder: "Bulle d'information affichée dans l'ENT",
    keyDef: keyDefs.String,
    keyUnit: '',
    keyValue: '',
    alone: true,
    readOnly: false,
    multiline: true,
  },
  {
    visible: true,
    keyName: 'createdAt',
    keyLabel: 'Créé le',
    keyDef: keyDefs.Date,
    keyUnit: '',
    keyValue: '',
    alone: true,
    readOnly: true,
  },
  {
    visible: true,
    keyName: 'updatedAt',
    keyLabel: 'Modifié le',
    keyDef: keyDefs.Date,
    keyUnit: '',
    keyValue: '',
    alone: true,
    readOnly: true,
  },
];

export const CouseContentsMetaDatas = (listOfSubjet: TableKeyValue[]): Property[] => {
  return [
    {
      visible: false,
      keyName: 'id',
      keyLabel: 'Id',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'contentNovadisId',
      keyLabel: 'Code Novadis',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: false,
      keyName: 'universalReference',
      keyLabel: 'Ref. transversalle',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'name',
      keyLabel: 'Nom',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
    },
    {
      visible: false,
      keyName: 'elearningProvider',
      keyLabel: 'Provider',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'description',
      keyLabel: 'Description',
      keyDef: keyDefs.String,
      placeholder: "Description du contenu affichée dans l'ENT",
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: false,
      multiline: true,
    },
    {
      visible: true,
      keyName: 'learningSubject',
      keyLabel: 'Matière',
      keyDef: keyDefs.Select,
      keyTable: listOfSubjet,
      keyUnit: '',
      keyValue: '',
      alone: false,
      readOnly: false,
      multiline: false,
    },
    {
      visible: true,
      keyName: 'printDate',
      keyLabel: 'Date imp.',
      tooltip: "Date d'impression",
      keyDef: keyDefs.DatePicker,
      keyUnit: '',
      keyValue: '',
      alone: false,
      readOnly: false,
    },
    {
      visible: true,
      keyName: 'stopDate',
      keyLabel: 'Date de fin',
      keyDef: keyDefs.DatePicker,
      keyUnit: '',
      keyValue: '',
      alone: false,
      readOnly: false,
    },
    {
      visible: true,
      keyName: 'releaseDate',
      keyLabel: 'Date de com.',
      tooltip: 'Date de commercialisation',
      keyDef: keyDefs.DatePicker,
      keyUnit: '',
      keyValue: '',
      alone: false,
      readOnly: false,
    },
    {
      visible: true,
      keyName: 'durationInMinutes',
      keyLabel: 'Durée',
      keyDef: keyDefs.Number,
      keyUnit: 'min',
      keyValue: 0,
      alone: true,
      readOnly: false,
    },
    {
      visible: true,
      keyName: 'phase',
      keyLabel: 'Phase',
      keyDef: keyDefs.Number,
      keyUnit: '',
      keyValue: 0,
      alone: false,
      readOnly: false,
    },
    {
      visible: true,
      keyName: 'displayableInDemo',
      keyLabel: 'Eligible démo',
      keyDef: keyDefs.Boolean,
      keyUnit: '',
      keyValue: 'false',
      alone: false,
    },
    {
      visible: true,
      keyName: 'isAdditionalContent',
      keyLabel: 'Contenu additionel',
      keyDef: keyDefs.Boolean,
      keyUnit: '',
      keyValue: 'false',
      alone: false,
    },
    {
      visible: false,
      keyName: 'unlockedBy',
      keyLabel: 'Débloqué par',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: 'false',
      alone: false,
    },
    {
      visible: true,
      keyName: 'createdAt',
      keyLabel: 'Créé le',
      keyDef: keyDefs.Date,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'updatedAt',
      keyLabel: 'Modifié le',
      keyDef: keyDefs.Date,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
  ];
};

export const CouseContentsMetaDatas2 = (listOfSubjet: TableKeyValue[]): Property[] => {
  return [
    {
      visible: false,
      keyName: 'id',
      keyLabel: 'Id',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'contentNovadisId',
      keyLabel: 'Code Novadis',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
      copiable: true,
    },
    {
      visible: false,
      keyName: 'universalReference',
      keyLabel: 'Ref. transversalle',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'name',
      keyLabel: 'Nom',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
    },
    {
      visible: false,
      keyName: 'elearningProvider',
      keyLabel: 'Provider',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'description',
      keyLabel: 'Description',
      keyDef: keyDefs.String,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: false,
      multiline: true,
    },
    {
      visible: true,
      keyName: 'learningSubject',
      keyLabel: 'Matière',
      keyDef: keyDefs.Select,
      keyTable: listOfSubjet,
      keyUnit: '',
      keyValue: '',
      alone: false,
      readOnly: false,
      multiline: false,
    },
    {
      visible: true,
      keyName: 'durationInMinutes',
      keyLabel: 'Durée',
      keyDef: keyDefs.Number,
      keyUnit: 'min',
      keyValue: 0,
      alone: true,
      readOnly: false,
    },
    // {
    //   visible: true,
    //   keyName: 'printDate',
    //   keyLabel: 'Date imp.',
    //   tooltip: "Date d'impression",
    //   keyDef: keyDefs.DatePicker,
    //   keyUnit: '',
    //   keyValue: '',
    //   alone: true,
    //   readOnly: false,
    // },
    // {
    //   visible: true,
    //   keyName: 'stopDate',
    //   keyLabel: 'Date de fin',
    //   keyDef: keyDefs.DatePicker,
    //   keyUnit: '',
    //   keyValue: '',
    //   alone: true,
    //   readOnly: false,
    // },
    // {
    //   visible: true,
    //   keyName: 'releaseDate',
    //   keyLabel: 'Date de com.',
    //   tooltip: 'Date de commercialisation',
    //   keyDef: keyDefs.DatePicker,
    //   keyUnit: '',
    //   keyValue: '',
    //   alone: true,
    //   readOnly: false,
    // },
    {
      visible: true,
      keyName: 'createdAt',
      keyLabel: 'Créé le',
      keyDef: keyDefs.Date,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
    {
      visible: true,
      keyName: 'updatedAt',
      keyLabel: 'Modifié le',
      keyDef: keyDefs.Date,
      keyUnit: '',
      keyValue: '',
      alone: true,
      readOnly: true,
    },
  ];
};
